import React, { useEffect, useState } from 'react';
import {
  X,
  Goal,
  Edit3,
  Workflow,
  Mail,
  FileText,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import {
  reviewCampaignRequest,
  selectReviewCampaignLoading,
  selectReviews,
} from 'store/campaigns';
import { CampaignReviewError, CampaignReviewWarning } from 'types';

interface ReviewErrorsPanelProps {
  onClose?: () => void;
  onViewMessage?: (nodeId: string) => void;
  campaignId: string;
  /** Show errors (default) or warnings */
  variant?: 'errors' | 'warnings';
}

interface ErrorCardProps {
  icon: React.ReactNode;
  title: string;
  message: string;
}

const ErrorCard: React.FC<ErrorCardProps> = ({ icon, title, message }) => (
  <div className="flex items-start gap-2 py-3 px-4 border-b border-border last:border-b-0">
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <p className="text-xs text-muted-foreground mt-0.5">{message}</p>
    </div>
  </div>
);

const ReviewErrorsPanel: React.FC<ReviewErrorsPanelProps> = ({
  onClose,
  onViewMessage,
  campaignId,
  variant = 'errors',
}) => {
  const dispatch = useDispatch();
  const reviews = useSelector(selectReviews);
  const reviewLoading = useSelector(selectReviewCampaignLoading);
  const [nameError, setNameError] = useState<CampaignReviewError | undefined>(
    undefined
  );
  const [goalError, setGoalError] = useState<CampaignReviewError | undefined>(
    undefined
  );
  const [workflowError, setWorkflowError] = useState<CampaignReviewError[]>([]);
  const warnings = (reviews?.warnings ?? []) as CampaignReviewWarning[];

  useEffect(() => {
    dispatch(reviewCampaignRequest(campaignId));
  }, [campaignId, dispatch]);

  useEffect(() => {
    if (reviews) {
      setNameError(reviews.errors.find((error) => error.type === 'name'));
      setGoalError(reviews.errors.find((error) => error.type === 'goal'));
      setWorkflowError(
        reviews.errors.filter((error) => error.type === 'workflow')
      );
    }
  }, [reviews]);

  const isWarnings = variant === 'warnings';

  if (reviewLoading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border w-80 overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center animate-pulse" />
            <h2 className="text-base font-semibold text-foreground">
              {isWarnings ? 'Warnings' : 'Review'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-4">
          <div className="animate-pulse space-y-2">
            <div className="h-3 bg-muted rounded w-3/4" />
            <div className="h-3 bg-muted rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (isWarnings) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border w-80 overflow-hidden">
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-amber-500/15 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Warnings
              </h2>
              <p className="text-xs text-muted-foreground">
                Optional items to review
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute right-2 top-2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="pb-4 border-t border-border">
          {warnings.length === 0 ? (
            <div className="py-6 px-4 text-center text-sm text-muted-foreground">
              No warnings
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[280px]">
              {warnings.map((warning, index) => (
                <div
                  key={warning.nodeId ?? `${warning.type}-${index}`}
                  className="flex items-center justify-between gap-2 py-3 px-4 border-b border-border last:border-b-0"
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground capitalize">
                        {warning.type}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {warning.message}
                      </p>
                    </div>
                  </div>
                  {warning.nodeId && (
                    <button
                      onClick={() => onViewMessage?.(warning.nodeId ?? '')}
                      className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                      aria-label="View"
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border w-80 overflow-hidden">
      {/* Header */}
      <div className="relative flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-primary/10 rounded-lg flex items-center justify-center">
            <AlertCircle className="w-4 h-4 text-warning-foreground" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Review</h2>
            <p className="text-xs text-muted-foreground">
              Items to fix before publishing
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className=" absolute right-2 top-2 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Error list */}
      <div className="pb-4 border-t border-border">
        {goalError?.type === 'goal' && goalError.message && (
          <ErrorCard
            icon={<Goal className="w-4 h-4 text-muted-foreground" />}
            title="Goal"
            message={goalError.message}
          />
        )}
        {nameError?.type === 'name' && nameError.message && (
          <ErrorCard
            icon={<Edit3 className="w-4 h-4 text-muted-foreground" />}
            title="Name"
            message={nameError.message}
          />
        )}

        {/* Workflow Section */}
        {workflowError.length > 0 && (
          <div className="py-3 px-4 border-b border-border last:border-b-0">
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                <Workflow className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-foreground">
                  Workflow
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Finish setting up {workflowError.length} message
                  {workflowError.length !== 1 ? 's' : ''} node to continue:
                </p>
                <div className="mt-2 space-y-1.5 overflow-y-auto max-h-[160px]">
                  {workflowError.map((error) => (
                    <div
                      key={error.nodeId ?? error.message}
                      className="flex items-center justify-between gap-2 p-2 bg-muted/50 border-l-4 border-primary rounded-r text-left"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className="text-xs text-foreground truncate">
                          {error.message}
                        </span>
                      </div>
                      <button
                        onClick={() => onViewMessage?.(error.nodeId ?? '')}
                        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                        aria-label="View message"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewErrorsPanel;
